import { generatePath, useNavigate } from 'react-router-dom';

import { useCancelOrder } from './useCancelOrder';
import { ORDER_ROUTES } from '../order.constants';
import { debug } from '../../../shared/lib/general';
import { clearAllSavedStates, clearSavedOrderState } from '../../../shared/lib/stateStorage';
import { useAppStore } from '../../../shared/hooks/useAppStore';
import { CancelPage, EVENT_NAMES, useSendEventLog } from '../../event-log';

export const useOrderActions = () => {
    const navigate = useNavigate();
    const order = useAppStore((s) => s.order);
    const vm = useAppStore((s) => s.vm);
    const qrCode = useAppStore((s) => s.qrCode);
    const { mutateAsync: cancelOrderMutate, isPending: isCancelling } = useCancelOrder();
    const { sendEvent } = useSendEventLog();

    // Cancel is awaited (mutateAsync) so the built-in CANCEL_SETTLE_DELAY_MS
    // in useCancelOrder runs BEFORE we navigate. Otherwise the user would
    // land on the canceled screen and fire a "Repeat purchase" /api/connect
    // inside the apparatus's cancel-processing window.
    const safelyCancelOrder = async (orderId: string | number) => {
        try {
            await cancelOrderMutate(String(orderId));
        } catch (e: unknown) {
            debug('cancel order failed', 'error', e);
            // Swallow: navigation should proceed regardless so the user
            // isn't stuck on the previous screen.
        }
    };

    // Common tail for both cancel paths: send Cashless:6, wait for the 2-second
    // apparatus-settle delay (built into useCancelOrder.mutationFn), wipe
    // saved state, navigate to the canceled screen.
    const cancelAndNavigate = async (routeId: string): Promise<void> => {
        if (order?.id) {
            await safelyCancelOrder(order.id);
        }
        clearAllSavedStates();
        navigate(generatePath(ORDER_ROUTES.canceled, { id: routeId }), {
            replace: true,
        });
    };

    const onCancelClickHandler = async (page: CancelPage): Promise<void> => {
        sendEvent({ name: EVENT_NAMES.cancel[page] });
        debug('cancel click', 'info');
        await cancelAndNavigate(String(order?.id));
    };

    const onTimerTimeoutHandler = async (
        page = '',
        shouldRedirect = true,
    ): Promise<void> => {
        debug(`timer timeout - ${page}`, 'info');
        if (shouldRedirect) {
            await cancelAndNavigate(String(order?.id ?? vm.qr));
        } else if (order?.id) {
            await safelyCancelOrder(order.id);
        }
    };

    const onError = (error: unknown, page = ''): void => {
        debug(`### Error: ${error} on page: ${page}`, 'error');
        let errorId: string = String(qrCode ?? vm.qr);
        if (!qrCode && (!vm.qr || vm.qr === 'qr')) {
            errorId = page !== '' ? page : 'app';
        }
        clearSavedOrderState();
        navigate(generatePath(ORDER_ROUTES.error, { id: errorId }), {
            replace: true,
        });
    };

    return {
        onCancelClickHandler,
        onTimerTimeoutHandler,
        onError,
        isCancelling,
    };
};
